<template>
  <DrawerPanel :model-value="isOpen" :title="title" @update:model-value="close">
    <div class="report-insights-drawer__body">
      <div class="report-insights-drawer__description" data-qa="report-insights-drawer-description" v-html="description"></div>
      <component
        v-if="hasCompleteCta"
          :is="resolvedAlertCtaTag"
          v-bind="resolvedAlertCtaProps"
          class="report-insights-drawer__cta-link"
          data-qa="report-insights-detail-drawer-cta"
          @click="onCtaClick"
      >
        <BaseButton class="report-insights-drawer__cta-button" element="span">
          {{ buttonCtaText }}
        </BaseButton>
      </component>
    </div>
  </DrawerPanel>
</template>

<script setup lang="ts">
import { useGtm } from '@gtm-support/vue-gtm';
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

import BaseButton from '@/components/base/BaseButton.vue';
import { useCreditReport } from '@/stores';
import { LOG_PREFIX } from '@/types/enums';
import { CreditReportAssetElement } from '@/types/responseTypes.js';
import { mapCtaUrlToRoute } from '@/utils/ctaUrlMapper.js';
import { getReportInsightsAssets } from '@/utils/helperFunctions';

import DrawerPanel from '../drawer-panel/DrawerPanel.vue';
const route = useRoute();
const router = useRouter();
const gtm = useGtm();

const creditReportStore = useCreditReport();
const isOpen = computed(() => creditReportStore.selectedReportInsight !== null);
const close = () => {
  creditReportStore.closeReportInsight();
};

const selectedReportInsight = computed<CreditReportAssetElement | null>(() => creditReportStore.selectedReportInsight);
const title = computed(() => selectedReportInsight.value?.link?.embeddedContent.title ?? 'Report Insight');
const description = computed(() => selectedReportInsight.value?.link?.embeddedContent.maximiserContent?.body);

const buttonCtaText = computed(() => selectedReportInsight.value?.link?.embeddedContent.maximiserContent?.ctaText);
const buttonCtaLink = computed(() => selectedReportInsight.value?.link?.embeddedContent.maximiserContent?.ctaLink);
const hasCtaText = computed(() => Boolean(buttonCtaText.value?.trim()));
const hasCtaLink = computed(() => Boolean(buttonCtaLink.value?.trim()));
const hasCompleteCta = computed(() => hasCtaText.value && hasCtaLink.value);
const reportInsightElements = computed(
  () => getReportInsightsAssets(creditReportStore.fullCreditProfile?.dashboard?.assets)?.elements ?? [],
);
const drawerOpenSource = ref<'dashboard' | 'direct_link' | null>(null);

watch(
  [() => route.query.reportInsightDetail, reportInsightElements],
  ([reportInsightDetail]) => {
    const reportInsightId = Array.isArray(reportInsightDetail)
      ? reportInsightDetail[0]
      : reportInsightDetail;

    if (!reportInsightId) return;

    const selectedInsight = reportInsightElements.value.find((element) => element.id === reportInsightId);
    if (!selectedInsight) return;

    drawerOpenSource.value = 'direct_link';
    creditReportStore.selectReportInsight(selectedInsight);

    const query = { ...route.query };
    delete query.reportInsightDetail;
    router.replace({ query });
  },
  { immediate: true },
);

watch(
  () => selectedReportInsight.value?.id,
  (reportInsightId) => {
    if (!reportInsightId) return;

    const source = drawerOpenSource.value ?? 'dashboard';

    gtm?.trackEvent({
      event: 'site_interaction',
      elementType: 'displayed',
      elementName: 'report-insight-drawer-view',
      elementText: reportInsightId,
      source,
    });

    drawerOpenSource.value = null;
  },
  { immediate: true },
);

watch([hasCtaText, hasCtaLink], ([isCtaTextAvailable, isCtaLinkAvailable]) => {
  if (isCtaTextAvailable !== isCtaLinkAvailable) {
    window.$log.error(`[${LOG_PREFIX.CREDIT_PROFILE}]: Report insight CTA is incomplete`, {
      ctaLink: buttonCtaLink.value,
      ctaText: buttonCtaText.value,
      reportInsightId: selectedReportInsight.value?.id,
    });
  }
}, { immediate: true });

const resolvedRoute = computed(() => {
  if (!hasCompleteCta.value) {
    return null;
  }

  const mappedRoute = mapCtaUrlToRoute(buttonCtaLink.value!, router);

  if (!mappedRoute) {
    return {
      text: buttonCtaText.value,
      href: buttonCtaLink.value,
    };  
  }

    return {
    text: buttonCtaText.value,
    to: mappedRoute,
  };
});

const resolvedAlertCtaTag = computed(() => resolvedRoute.value && 'to' in resolvedRoute.value ? RouterLink : 'a');
const resolvedAlertCtaProps = computed(() => {
  if (!resolvedRoute.value) {
    return {};
  }

  return 'to' in resolvedRoute.value
    ? { to: resolvedRoute.value.to }
    : { href: resolvedRoute.value.href };
});


const onCtaClick = () => {
  gtm?.trackEvent({
    event: 'simple_interaction',
    elementType: 'button',
    elementName: 'report-insight-cta-click',
    elementText: selectedReportInsight.value?.id,
  });

  close();
};

</script>

<style scoped lang="scss">
.report-insights-drawer {
  &__body {
    padding: 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__description {
    color: $brand-primary-100-navy;
    font-size: remc(16px);
    line-height: remc(24px);
    margin: 0;

    :deep(p, h3, h4, h5, h6) {
      margin-bottom: 1.5rem;
    }
  }

  &__cta-link {
    text-decoration: none;
  }

  &__cta-button {
    width: 100%;
  }
}
</style>
